import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ShieldCheck } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/use-auth';

interface PrivacyDoc {
  content: string;
  updatedAt: string;
}

/**
 * Modal bloqueante que aparece cuando el usuario autenticado todavía no ha
 * aceptado la política de privacidad (`user.privacyPolicyAcceptedAt === null`).
 * Carga el markdown desde GET /legal/privacy y lo renderiza. El usuario no
 * puede navegar por la app hasta pulsar "Acepto".
 *
 * Cumple Art. 13 RGPD (información en el momento de obtener los datos): aunque
 * los datos del empleado ya estaban en BD (alta por RR. HH.), el primer
 * acceso a la aplicación es el momento práctico de informarle.
 */
export function PrivacyPolicyGate() {
  const { user, acceptPrivacyPolicy } = useAuth();
  const [doc, setDoc] = useState<PrivacyDoc | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const needsAcceptance = user !== null && !user.privacyPolicyAcceptedAt;

  useEffect(() => {
    if (!needsAcceptance) return;
    let cancelled = false;
    api<PrivacyDoc>('/legal/privacy')
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Error cargando la política';
          setLoadError(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [needsAcceptance]);

  if (!needsAcceptance) return null;

  async function onAccept() {
    setAccepting(true);
    setAcceptError(null);
    try {
      await acceptPrivacyPolicy();
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Error al aceptar');
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: 'rgba(10,14,31,0.45)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-gate-title"
    >
      <div className="bg-card border border-border rounded-sm shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        <header className="px-7 py-5 border-b border-border flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-icaria-600 shrink-0" />
          <h2 id="privacy-gate-title" className="text-lg font-semibold tracking-tightish">
            Política de privacidad
          </h2>
        </header>

        <div className="flex-1 overflow-y-auto px-7 py-5">
          {loadError ? (
            <p className="text-sm text-destructive">No se pudo cargar la política: {loadError}</p>
          ) : !doc ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <div className="prose prose-sm max-w-none text-[14px] leading-relaxed">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
            </div>
          )}
        </div>

        <footer className="px-7 py-4 border-t border-border bg-paper flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[12px] text-muted-foreground">
            Para usar CheckIn_App debes leer y aceptar la política. Si tienes dudas, contacta con
            RR. HH.
          </p>
          <div className="flex items-center gap-3">
            {acceptError ? <span className="text-[12px] text-destructive">{acceptError}</span> : null}
            <Button type="button" variant="primary" onClick={onAccept} disabled={accepting || !doc}>
              {accepting ? 'Guardando…' : 'He leído y acepto'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
